import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction } from '@lyearn/i18n';
import {
  ArchiveOutline,
  BellOutline,
  CloudUpload,
  EditOutline,
  EmailOutline,
  EyeOutline,
  GlobeFill,
  KeyOutline,
  Link2Outline,
  MaskOutline,
  PeopleOutline,
  PrinterOutline,
  SlashOutline,
  Undo2Outline,
} from '@lyearn/icons';
import { ActionMenuProps } from '@lyearn/molecules';
import { tagsFilterConfig } from '@/components/FilterManager/configs/tags/tagsFilterConfig';
import { DepartmentEntityConfig } from '@/components/FilterManager/EntityFilter/entity/department';
import { OrgRoleEntityConfig } from '@/components/FilterManager/EntityFilter/entity/org-role';
import { StaffRoleEntityConfig } from '@/components/FilterManager/EntityFilter/entity/staff-role';
import {
  DEFAULT_SORT_CONFIG,
  getAlphabeticallySortItem,
  getNewestSortItem,
  getOldestSortItem,
} from '@/components/FilterManager/SortMenu/utils';
import { FilterManager, SortAndFilterItem, SortEnums } from '@/components/FilterManager/types';
import { OrderEnum, TagAvailableOnEnum, UserStatusEnum, UserTypeEnum } from '@/types/schema';

import { LearnerAction, LearnerPageFilters, LearnersActions, LearnerStatusFilters } from '../types';

const LEARNER_SORT_CONFIG = {
  ...DEFAULT_SORT_CONFIG,
  [SortEnums.Alphabetically]: [
    { field: 'name', order: OrderEnum.Asc },
    { field: 'emailId', order: OrderEnum.Asc },
  ],
};

export const getLearnersSortDefaultValue = (status: LearnerStatusFilters) =>
  status === LearnerStatusFilters.PENDING
    ? LEARNER_SORT_CONFIG[SortEnums.Newest]
    : LEARNER_SORT_CONFIG[SortEnums.Alphabetically];

export const MAPPED_STATUS_TO_ENUM = {
  [LearnerStatusFilters.ACTIVE]: UserStatusEnum.Active,
  [LearnerStatusFilters.PENDING]: UserStatusEnum.Invited,
  [LearnerStatusFilters.DEACTIVATED]: UserStatusEnum.Archived,
};

const getLearnerSortItems = (status: LearnerStatusFilters) =>
  status === LearnerStatusFilters.PENDING
    ? [
        getAlphabeticallySortItem({ sort: LEARNER_SORT_CONFIG[SortEnums.Alphabetically] }),
        getNewestSortItem(),
        getOldestSortItem(),
      ]
    : [
        getNewestSortItem(),
        getOldestSortItem(),
        getAlphabeticallySortItem({ sort: LEARNER_SORT_CONFIG[SortEnums.Alphabetically] }),
      ];

export const getSortAndFilterItems = (
  t: TFunction,
  pageFilter: LearnerPageFilters,
  status: LearnerStatusFilters,
): SortAndFilterItem[] => {
  return [
    {
      type: FilterManager.Sort,
      isEnabled: true,
      defaultSortById:
        status === LearnerStatusFilters.PENDING ? SortEnums.Newest : SortEnums.Alphabetically,
      items: getLearnerSortItems(status),
    },
    {
      type: FilterManager.Entity,
      isEnabled: pageFilter === LearnerPageFilters.DEFAULT,
      defaultPlaceholder: t('Common:Filter.Department'),
      listConfig: DepartmentEntityConfig,
      id: 'departmentIds',
    },
    {
      type: FilterManager.Entity,
      isEnabled: pageFilter === LearnerPageFilters.DEFAULT,
      defaultPlaceholder: t('Common:Filter.Designation'),
      listConfig: OrgRoleEntityConfig,
      id: 'orgRoleIds',
    },
    {
      type: FilterManager.DateRange,
      id: 'createdAt',
      defaultPlaceholder: t('Common:Filter.JoinedOn'),
      isEnabled: true,
    },
    {
      type: FilterManager.Entity,
      isEnabled: pageFilter === LearnerPageFilters.STAFF,
      listConfig: StaffRoleEntityConfig,
      defaultPlaceholder: t('Common:Filter.StaffRole'),
      id: 'staffRoleIds',
    },
    {
      ...tagsFilterConfig(t),
      queryParams: {
        availableOn: [TagAvailableOnEnum.User],
      },
    },
  ];
};

export const getUserActionItems = (
  t: TFunction,
  status: UserStatusEnum | null,
  pageFilter: LearnerPageFilters,
  isSelf: boolean,
  canMasquerade: boolean,
  canMergeAccount: boolean,
): ActionMenuProps['items'] => {
  if (!status) {
    return [];
  }

  return getEnabledItems([
    {
      id: LearnerAction.ViewProfile,
      label: t('Common:Actions.ViewProfile'),
      EndIcon: EyeOutline,
      enabled: true,
    },
    {
      id: LearnerAction.ReactivateLearner,
      EndIcon: Undo2Outline,
      label: t('Common:Actions.Reactivate'),
      enabled: status === UserStatusEnum.Archived,
    },
    {
      id: LearnerAction.EditProfile,
      label: t('Common:EditProfile'),
      EndIcon: EditOutline,
      enabled: status !== UserStatusEnum.Archived,
    },
    {
      id: LearnerAction.ShareLearnerProfile,
      EndIcon: Link2Outline,
      label: t('Common:Copylink'),
      enabled: true,
    },
    {
      id: LearnerAction.EditRole,
      label: t('Common:Actions.EditRole'),
      EndIcon: KeyOutline,
      enabled: pageFilter === LearnerPageFilters.STAFF,
    },
    {
      id: LearnerAction.Masquerade,
      label: t('Common:Actions.Masquerade'),
      EndIcon: MaskOutline,
      enabled: pageFilter === LearnerPageFilters.DEFAULT && canMasquerade,
    },
    {
      id: LearnerAction.ResendInvite,
      label: t('Common:Actions.ResendInvite'),
      EndIcon: EmailOutline,
      enabled: status === UserStatusEnum.Invited,
    },
    {
      id: LearnerAction.CancelInvite,
      label: t('Common:Actions.CancelInvite'),
      EndIcon: SlashOutline,
      enabled: status === UserStatusEnum.Invited,
      appearance: 'danger',
    },
    {
      id: LearnerAction.MergeAccount,
      label: t('Common:Actions.MergeAccount'),
      EndIcon: PeopleOutline,
      appearance: 'danger',
      enabled: canMergeAccount,
    },
    {
      id: LearnerAction.DeactivateStaff,
      label: t('Common:Actions.Deactivate'),
      EndIcon: ArchiveOutline,
      enabled: pageFilter === LearnerPageFilters.STAFF && status === UserStatusEnum.Active,
      appearance: 'danger',
    },
    {
      id: LearnerAction.DeactivateLearner,
      label: t('Common:Actions.Deactivate'),
      EndIcon: ArchiveOutline,
      enabled:
        !isSelf && pageFilter !== LearnerPageFilters.STAFF && status === UserStatusEnum.Active,
      appearance: 'danger',
    },
    // {
    //   id: LearnerAction.EditTags,
    //   label: t('Common:Actions.EditTags'),
    //   EndIcon: TagOutline,
    //   enabled: pageFilter === LearnerPageFilters.DEFAULT,
    // },
    // {
    //   id: LearnerAction.Assign,
    //   label: t('Common:Actions.Assign'),
    //   EndIcon: File,
    //   enabled: status === UserStatusEnum.Active,
    // },
    // {
    //   id: LearnerAction.ReActivate,
    //   label: t('Common:Actions.ReActivate'),
    //   EndIcon: Undo,
    //   enabled: status === UserStatusEnum.Archived,
    // },
  ]) as ActionMenuProps['items'];
};

// todo: remove
export const INITIAL_FILTERS = {
  [LearnerPageFilters.DEFAULT]: {
    userTypes: [UserTypeEnum.Learner],
  },
  [LearnerPageFilters.STAFF]: {
    userTypes: [UserTypeEnum.Staff],
  },
};

export const getInvitedDuration = (date: string) => {
  return formatDistanceToNowStrict(new Date(date));
};

export const getActionMenuItems = (props: {
  t: TFunction;
  status: LearnerStatusFilters;
  hasLearners: boolean;
}) => {
  const { t, status, hasLearners } = props;
  return [
    {
      id: LearnersActions.InviteExternalLearners,
      label: t('Admin:ExternalLearners.InviteExternalEmployees'),
      EndIcon: GlobeFill,
    },
    {
      id: LearnersActions.BulkImport,
      label: t('Admin:BulkImport.ImportEmployees'),
      EndIcon: CloudUpload,
    },
    {
      id: LearnersActions.BulkExport,
      label: t('Admin:BulkExport.ExportEmployees'),
      EndIcon: PrinterOutline,
    },
    {
      id: LearnersActions.ResendInvitations,
      label: t('Admin:LearnerAndGroups.ResendInvitations'),
      EndIcon: BellOutline,
      enabled: status === LearnerStatusFilters.PENDING && hasLearners,
    },
  ] as ActionMenuProps['items'];
};
